import React, { useEffect, useReducer, useState } from 'react';
import api from '../api';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getError, materialSaveError } from '../utils';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import LoadingLogo from '../components/general/LoadingLogo';
import MessageBox from '../components/general/MessageBox';
import Button from 'react-bootstrap/Button';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import {
  adjustScrap,
  adjustStock,
  stockQuantitiesBody,
} from '../utils/stockAdjustments';
import { addSelectedOptions } from '../utils/formInputs';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS':
      return { ...state, loading: false };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };

    case 'UPDATE_REQUEST':
      return { ...state, loadingUpdate: true };
    case 'UPDATE_SUCCESS':
      return { ...state, loadingUpdate: false };
    case 'UPDATE_FAIL':
      return { ...state, loadingUpdate: false };

    default:
      return state;
  }
};

export default function KeycapEditScreen() {
  const navigate = useNavigate();
  const params = useParams();
  const { search } = useLocation();
  const stockPage = new URLSearchParams(search).get('page') || 1; // the stock list's page this item was opened from, to return to after the save
  const { id: keycapId } = params;

  const [{ loading, error, loadingUpdate }, dispatch] = useReducer(reducer, {
    loading: true,
    error: '',
  });

  const [name, setName] = useState('');
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [widthMM, setWidthMM] = useState(0);
  const [lengthMM, setLengthMM] = useState(0);
  const [heightMM, setHeightMM] = useState(0);
  const [weight, setWeight] = useState(0);
  const [isConvex, setIsConvex] = useState(false);
  const [isOffCentered, setIsOffCentered] = useState(false);
  const [isIsoEnter, setIsIsoEnter] = useState(false);
  const [hasHomingBar, setHasHomingBar] = useState(false);
  const [isStepped, setIsStepped] = useState(false);
  const [profile, setProfile] = useState('');
  const [row, setRow] = useState('');
  const [color, setColor] = useState('');
  const [material, setMaterial] = useState('');
  const [stockQuantity, setStockQuantity] = useState(0);
  const [originalStockQuantity, setOriginalStockQuantity] = useState(0);
  const [stockAdjustment, setStockAdjustment] = useState(0);
  const [reservedQuantity, setReservedQuantity] = useState(0);
  const [originalReservedQuantity, setOriginalReservedQuantity] = useState(0);
  const [scrapQuantity, setScrapQuantity] = useState(0);
  const [originalScrapQuantity, setOriginalScrapQuantity] = useState(0);
  const [scrapAdjustment, setScrapAdjustment] = useState(0);
  const [soldQuantity, setSoldQuantity] = useState(0);
  const [originalSoldQuantity, setOriginalSoldQuantity] = useState(0);
  const [manufacturer, setManufacturer] = useState('');
  const [switchCompatibility, setSwitchCompatibility] = useState([]);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch({ type: 'FETCH_REQUEST' });
        const { data } = await api.get(`/api/keycaps/${keycapId}`);
        setName(data.name);
        setWidth(data.width);
        setHeight(data.height);
        setWidthMM(data.widthMM);
        setLengthMM(data.lengthMM);
        setHeightMM(data.heightMM);
        setWeight(data.weight);
        setIsConvex(data.isConvex);
        setIsOffCentered(data.isOffCentered);
        setIsIsoEnter(data.isIsoEnter);
        setHasHomingBar(data.hasHomingBar);
        setIsStepped(data.isStepped);
        setProfile(data.profile);
        setRow(data.row);
        setColor(data.color);
        setMaterial(data.material);
        setStockQuantity(data.stockQuantity);
        setOriginalStockQuantity(data.stockQuantity);
        setReservedQuantity(data.reservedQuantity);
        setOriginalReservedQuantity(data.reservedQuantity);
        setScrapQuantity(data.scrapQuantity);
        setOriginalScrapQuantity(data.scrapQuantity);
        setSoldQuantity(data.soldQuantity);
        setOriginalSoldQuantity(data.soldQuantity);
        setManufacturer(data.manufacturer);
        setSwitchCompatibility(data.switchCompatibility);
        setIsActive(data.isActive);

        dispatch({ type: 'FETCH_SUCCESS' });
      } catch (err) {
        dispatch({
          type: 'FETCH_FAIL',
          payload: getError(err),
        });
      }
    };
    fetchData();
  }, [keycapId]);

  const submitHandler = async (e) => {
    e.preventDefault(); // used, so the page doesn't refresh, when the submit button is clicked
    try {
      dispatch({ type: 'UPDATE_REQUEST' });

      await api.put(`/api/keycaps/${keycapId}`, {
        name: name,
        width: width,
        height: height,
        widthMM: widthMM,
        lengthMM: lengthMM,
        heightMM: heightMM,
        weight,
        isConvex: isConvex,
        isOffCentered: isOffCentered,
        isIsoEnter: isIsoEnter,
        hasHomingBar: hasHomingBar,
        isStepped: isStepped,
        profile: profile,
        row: row,
        color: color.toUpperCase(),
        material: material,
        ...stockQuantitiesBody({
          stockQuantity,
          originalStockQuantity,
          reservedQuantity,
          originalReservedQuantity,
          scrapQuantity,
          originalScrapQuantity,
          soldQuantity,
          originalSoldQuantity,
        }), // the quantities beside the values the screen loaded, which the route compares before it writes
        manufacturer: manufacturer,
        switchCompatibility: switchCompatibility,
        isActive: isActive,
      });
      dispatch({
        type: 'UPDATE_SUCCESS',
      });
      toast.success('Keycap gespeichert');
      navigate(`/admin/stock?filter=keycaps&page=${stockPage}`);
    } catch (err) {
      toast.error(materialSaveError(err)); // the 409 of a document changed meanwhile, else what the request says
      dispatch({ type: 'UPDATE_FAIL' });
    }
  };

  const handleSwitchCompatibilityChange = (e) => {
    setSwitchCompatibility(
      addSelectedOptions(switchCompatibility, e.target.selectedOptions)
    ); // adds the selected options once each
  };

  const removeCompatibility = (item) => {
    setSwitchCompatibility(switchCompatibility.filter((comp) => comp !== item));
  }; // removes an item from the switchCompatibility

  const handleWheel = (e) => {
    e.target.blur(); // Unfocus the input to prevent scrolling from changing the value
    e.preventDefault(); // Prevent the default scroll behavior
    setTimeout(() => e.target.focus(), 0); // Refocus it after the wheel event
  };

  const handleScrapAdjustment = () => {
    const next = adjustScrap(
      { stockQuantity, reservedQuantity, scrapQuantity },
      scrapAdjustment
    );
    if (next.error) toast.error(next.error);
    setScrapQuantity(next.scrapQuantity);
    setStockQuantity(next.stockQuantity);
    setScrapAdjustment(next.scrapAdjustment);
  };

  const handleStockAdjustment = () => {
    const next = adjustStock(
      { stockQuantity, reservedQuantity },
      stockAdjustment
    );
    if (next.error) toast.error(next.error);
    setStockQuantity(next.stockQuantity);
    setStockAdjustment(next.stockAdjustment);
  };

  return (
    <Container>
      <Container className="small-container">
        <title>Keycap bearbeiten</title>

        <h1 className="screen-heading">Keycap bearbeiten</h1>

        {loading || loadingUpdate ? (
          <div className="overlay">
            <LoadingLogo />
          </div>
        ) : error ? (
          <MessageBox className="danger-alert">{error}</MessageBox>
        ) : (
          <Form onSubmit={submitHandler}>
            <Form.Group className="mb-3" controlId="name">
              <Form.Label>Name</Form.Label>
              <Form.Control
                className="mobile-device-compatible"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="profile">
              <Form.Label>Profile</Form.Label>
              <Form.Select
                className="mobile-device-compatible"
                value={profile}
                onChange={(e) => setProfile(e.target.value)}
                required
              >
                <option className="option-tag" value="SA">
                  SA
                </option>
                <option className="option-tag" value="OEM">
                  OEM
                </option>
                <option className="option-tag" value="Cherry">
                  Cherry
                </option>
                <option className="option-tag" value="DSA">
                  DSA
                </option>
                <option className="option-tag" value="XDA">
                  XDA
                </option>
                <option className="option-tag" value="Apple MacBook">
                  Apple MacBook
                </option>
                <option className="option-tag" value="Apple Magic Keyboard">
                  Apple Magic Keyboard
                </option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3" controlId="width">
              <Form.Label>Width</Form.Label>
              <Form.Select
                className="mobile-device-compatible"
                value={width}
                onChange={(e) => setWidth(e.target.value)}
                required
              >
                <option className="option-tag" value={1}>
                  1
                </option>
                <option className="option-tag" value={1.25}>
                  1.25
                </option>
                <option className="option-tag" value={1.5}>
                  1.5
                </option>
                <option className="option-tag" value={1.75}>
                  1.75
                </option>
                <option className="option-tag" value={2}>
                  2
                </option>
                <option className="option-tag" value={2.25}>
                  2.25
                </option>
                <option className="option-tag" value={2.75}>
                  2.75
                </option>
                <option className="option-tag" value={3}>
                  3
                </option>
                <option className="option-tag" value={3.25}>
                  3.25
                </option>
                <option className="option-tag" value={4.5}>
                  4.5
                </option>
                <option className="option-tag" value={6}>
                  6
                </option>
                <option className="option-tag" value={6.25}>
                  6.25
                </option>
                <option className="option-tag" value={7}>
                  7
                </option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3" controlId="height">
              <Form.Label>Height</Form.Label>
              <Form.Select
                className="mobile-device-compatible"
                value={height}
                onChange={(e) => setHeight(e.target.value)}
                required
              >
                <option className="option-tag" value={1}>
                  1
                </option>
                <option className="option-tag" value={1.25}>
                  1.25
                </option>
                <option className="option-tag" value={1.5}>
                  1.5
                </option>
                <option className="option-tag" value={1.75}>
                  1.75
                </option>
                <option className="option-tag" value={2}>
                  2
                </option>
                <option className="option-tag" value={2.25}>
                  2.25
                </option>
                <option className="option-tag" value={2.5}>
                  2.5
                </option>
                <option className="option-tag" value={2.75}>
                  2.75
                </option>
                <option className="option-tag" value={3}>
                  3
                </option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3" controlId="widthMM">
              <Form.Label className="d-flex">Width in mm</Form.Label>
              <Form.Control
                className="mobile-device-compatible"
                type="Number"
                value={widthMM}
                onChange={(e) => setWidthMM(e.target.value)}
                onWheel={handleWheel}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="lengthMM">
              <Form.Label className="d-flex">Length in mm</Form.Label>
              <Form.Control
                className="mobile-device-compatible"
                type="Number"
                value={lengthMM}
                onChange={(e) => setLengthMM(e.target.value)}
                onWheel={handleWheel}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="heightMM">
              <Form.Label className="d-flex">Height in mm</Form.Label>
              <Form.Control
                className="mobile-device-compatible"
                type="Number"
                value={heightMM}
                onChange={(e) => setHeightMM(e.target.value)}
                onWheel={handleWheel}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="weight">
              <Form.Label className="d-flex">
                Weight (g)
                <OverlayTrigger
                  trigger="click"
                  rootClose
                  placement="right"
                  overlay={
                    <Popover>
                      <Popover.Body>
                        Bezieht sich auf das Gewicht einer einzelnen Tastenkappe
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <Button className="info-popover">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="#707070"
                      className="bi bi-info-circle"
                      viewBox="0 0 16 16"
                    >
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                      <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                    </svg>
                  </Button>
                </OverlayTrigger>
              </Form.Label>

              <Form.Control
                className="mobile-device-compatible"
                type="number"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                onWheel={handleWheel}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="isConvex">
              <Form.Label>Convex</Form.Label>
              <Form.Select
                className="mobile-device-compatible"
                value={isConvex}
                onChange={(e) => setIsConvex(e.target.value)}
                required
              >
                <option className="option-tag" value={true}>
                  Yes
                </option>
                <option className="option-tag" value={false}>
                  No
                </option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3" controlId="isOffCentered">
              <Form.Label>Off-Centered</Form.Label>
              <Form.Select
                className="mobile-device-compatible"
                value={isOffCentered}
                onChange={(e) => setIsOffCentered(e.target.value)}
                required
              >
                <option className="option-tag" value={true}>
                  Yes
                </option>
                <option className="option-tag" value={false}>
                  No
                </option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3" controlId="isIsoEnter">
              <Form.Label>ISO Enter</Form.Label>
              <Form.Select
                className="mobile-device-compatible"
                value={isIsoEnter}
                onChange={(e) => setIsIsoEnter(e.target.value)}
                required
              >
                <option className="option-tag" value={true}>
                  Yes
                </option>
                <option className="option-tag" value={false}>
                  No
                </option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3" controlId="hasHomingBar">
              <Form.Label>Homing Bar</Form.Label>
              <Form.Select
                className="mobile-device-compatible"
                value={hasHomingBar}
                onChange={(e) => setHasHomingBar(e.target.value)}
                required
              >
                <option className="option-tag" value={true}>
                  Yes
                </option>
                <option className="option-tag" value={false}>
                  No
                </option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3" controlId="isStepped">
              <Form.Label>Stepped</Form.Label>
              <Form.Select
                className="mobile-device-compatible"
                value={isStepped}
                onChange={(e) => setIsStepped(e.target.value)}
                required
              >
                <option className="option-tag" value={true}>
                  Yes
                </option>
                <option className="option-tag" value={false}>
                  No
                </option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3" controlId="color">
              <Form.Label>Color</Form.Label>
              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                <Form.Control
                  type="text"
                  className="mobile-device-compatible"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$"
                  placeholder="#FFFFFF"
                  required
                  style={{ marginRight: '10px' }}
                />
                <div
                  style={{
                    width: '39px',
                    height: '30px',
                    backgroundColor: color,
                  }}
                />
              </div>
            </Form.Group>
            <Form.Group className="mb-3" controlId="row">
              <Form.Label>Row</Form.Label>
              <Form.Select
                className="mobile-device-compatible"
                value={row}
                onChange={(e) => setRow(e.target.value)}
                required
              >
                <option className="option-tag" value="Uniform">
                  Uniform
                </option>
                <option className="option-tag" value="R1">
                  R1
                </option>
                <option className="option-tag" value="R1-1">
                  R1-1
                </option>
                <option className="option-tag" value="R1-2">
                  R1-2
                </option>
                <option className="option-tag" value="R2">
                  R2
                </option>
                <option className="option-tag" value="R2-3">
                  R2-3
                </option>
                <option className="option-tag" value="R3">
                  R3
                </option>
                <option className="option-tag" value="R3-4">
                  R3-4
                </option>
                <option className="option-tag" value="R4">
                  R4
                </option>
                <option className="option-tag" value="R4-4">
                  R4-4
                </option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3" controlId="material">
              <Form.Label>Material</Form.Label>
              <Form.Select
                className="mobile-device-compatible"
                value={material}
                onChange={(e) => setMaterial(e.target.value)}
                required
              >
                <option className="option-tag" value="PBT">
                  PBT
                </option>
                <option className="option-tag" value="ABS">
                  ABS
                </option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3" controlId="stock">
              <Form.Label className="d-flex">
                Stock
                <OverlayTrigger
                  trigger="click"
                  rootClose // close the popover when the user clicks outside of it
                  placement="right"
                  overlay={
                    <Popover id="popover-password">
                      <Popover.Body>
                        Die Anzahl an Keycaps, die sich aktuell im Lager
                        befinden, einschließlich bereits reservierter Keycaps.
                        (stockQuantity - reservedQuantity = [quantity that is
                        not sold yet])
                        <br />
                        <br />
                        Der Stock kann nicht kleiner sein als Reserved, da die
                        reservedQuantity Teil der stockQuantity ist.
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <Button className="info-popover">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="#707070"
                      className="bi bi-info-circle"
                      viewBox="0 0 16 16"
                    >
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                      <path
                        d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469
                l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252
                1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0
                -.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0
                1 1 0 0 1 2 0z"
                      />
                    </svg>
                  </Button>
                </OverlayTrigger>
              </Form.Label>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '15px',
                }}
              >
                <p style={{ margin: '0px 10px 0px 20px' }}>
                  <span>{stockQuantity}</span> pieces
                </p>

                <div className="d-flex">
                  {/* Adjustment field for the user to enter +/- changes */}
                  <Form.Control
                    className="mobile-device-compatible"
                    type="number"
                    value={stockAdjustment}
                    onChange={(e) => setStockAdjustment(e.target.value)}
                    onWheel={handleWheel}
                  />

                  {/* A button that applies the above logic */}
                  <Button variant="secondary" onClick={handleStockAdjustment}>
                    {stockAdjustment >= 0 ? 'Add' : 'Subtract'}
                  </Button>
                </div>
              </div>
            </Form.Group>

            <Form.Group className="mb-3" controlId="reserved">
              <Form.Label className="d-flex">
                Reserved
                <OverlayTrigger
                  trigger="click"
                  rootClose // close the popover when the user clicks outside of it
                  placement="right"
                  overlay={
                    <Popover id="popover-password">
                      <Popover.Body>
                        Die Menge an keycaps, die bereits für aufgegebene
                        Bestellungen reserviert sind (stockQuantity -
                        reservedQuantity = [quantity that is not sold yet])
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <Button className="info-popover">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="#707070"
                      className="bi bi-info-circle"
                      viewBox="0 0 16 16"
                    >
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                      <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                    </svg>
                  </Button>
                </OverlayTrigger>
              </Form.Label>

              <p style={{ margin: '0px 10px 0px 20px', fontSize: '15px' }}>
                <span>{reservedQuantity}</span> pieces
              </p>
            </Form.Group>

            <Form.Group className="mb-3" controlId="scrap">
              <Form.Label className="d-flex">
                Scrap
                <OverlayTrigger
                  trigger="click"
                  rootClose // close the popover when the user clicks outside of it
                  placement="right"
                  overlay={
                    <Popover id="popover-password">
                      <Popover.Body>
                        <strong>
                          Die Anzahl der Keycaps, die während der Produktion
                          unbrauchbar geworden sind.
                        </strong>
                        <br />
                        <br />
                        Beim Hinzufügen zu **Scrap** kann ausschließlich der
                        freie Lagerbestand (Stock − Reserved) reduziert werden.
                        Übersteigt die hinzugefügte Menge den freien
                        Lagerbestand, wird die Änderung nicht vorgenommen.
                        Reservierungen bleiben dabei immer unangetastet.
                        <br />
                        <br />
                        Wenn eine Menge zu den Scraps hinzugefügt wird, wird
                        dieselbe Menge vom Stock abgezogen.
                        <br />
                        <br />
                        Umgekehrt wird beim Reduzieren der Scrap-Menge dieselbe
                        Menge wieder dem Stock hinzugefügt.
                        <br />
                        <br />
                        Falls nicht genügend Stock verfügbar ist, kann die
                        Anpassung nicht vorgenommen werden.
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <Button className="info-popover">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="#707070"
                      className="bi bi-info-circle"
                      viewBox="0 0 16 16"
                    >
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                      <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                    </svg>
                  </Button>
                </OverlayTrigger>
              </Form.Label>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '15px',
                }}
              >
                <p style={{ margin: '0px 10px 0px 20px' }}>
                  <span style={{ color: 'red' }}>{scrapQuantity}</span> pieces
                </p>

                <div className="d-flex">
                  {/* The numeric field where the user enters the +/- adjustment */}
                  <Form.Control
                    className="mobile-device-compatible"
                    type="number"
                    value={scrapAdjustment}
                    onChange={(e) => setScrapAdjustment(e.target.value)}
                    onWheel={handleWheel}
                  />

                  {/* A button that applies the above logic */}
                  <Button variant="secondary" onClick={handleScrapAdjustment}>
                    {scrapAdjustment >= 0 ? 'Add' : 'Subtract'}
                  </Button>
                </div>
              </div>
            </Form.Group>

            <Form.Group className="mb-3" controlId="sold">
              <Form.Label className="d-flex">Sold</Form.Label>
              <p style={{ margin: '0px 10px 0px 20px', fontSize: '15px' }}>
                <span style={{ color: 'green' }}>{soldQuantity}</span> pieces
              </p>
            </Form.Group>

            <Form.Group className="mb-3" controlId="manufacturer">
              <Form.Label>Manufacturer</Form.Label>
              <Form.Control
                className="mobile-device-compatible"
                value={manufacturer}
                onChange={(e) => setManufacturer(e.target.value)}
                required
              />
            </Form.Group>

            <Form className="mb-3">
              <Form.Group className="mb-2" controlId="switchCompatibility">
                <Form.Label>Switch Compatibility</Form.Label>
                <Form.Select
                  className="mobile-device-compatible"
                  onChange={handleSwitchCompatibilityChange}
                  required
                >
                  <option value="Cherry MX">Cherry MX</option>
                  <option value="Topre">Topre</option>
                  <option value="Alps">Alps</option>
                  <option value="Buckling Spring">Buckling Spring</option>
                </Form.Select>
              </Form.Group>
              <div className="selected-items">
                {switchCompatibility.map((item, index) => (
                  <div
                    key={index}
                    className="selected-item"
                    style={{ fontSize: '14px', marginLeft: '10px' }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="10"
                      height="10"
                      fill="currentColor"
                      className="bi bi-x-lg"
                      viewBox="0 0 16 16"
                      onClick={() => removeCompatibility(item)}
                      style={{ cursor: 'pointer' }}
                    >
                      <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8z" />
                    </svg>{' '}
                    {item}
                  </div>
                ))}
              </div>
            </Form>
            <Form.Group className="mb-3" controlId="isActive">
              <Form.Label className="d-flex">
                Active
                <OverlayTrigger
                  trigger="click"
                  rootClose // close the popover when the user clicks outside of it
                  placement="right"
                  overlay={
                    <Popover>
                      <Popover.Body>
                        Bestimmt, ob diese Tastenkappe für die berechnung der
                        stockQuantities der Layout documents mit einbezogen
                        werden soll.
                        <br />
                        <br />
                        Wenn nein, dann sind alle Layouts die diese Tastenkappe
                        verwenden als ausverkauft markiert.
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <Button className="info-popover">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="#707070"
                      className="bi bi-info-circle"
                      viewBox="0 0 16 16"
                    >
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                      <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                    </svg>
                  </Button>
                </OverlayTrigger>
              </Form.Label>
              <Form.Select
                className="mobile-device-compatible"
                value={isActive}
                onChange={(e) => setIsActive(e.target.value)}
                required
              >
                <option className="option-tag" value={true}>
                  Yes
                </option>
                <option className="option-tag" value={false}>
                  No
                </option>
              </Form.Select>
            </Form.Group>

            <div className="d-flex justify-content-center align-items-center mt-5">
              <Button
                variant="secondary"
                className="mx-3"
                disabled={loadingUpdate}
                type="submit"
              >
                {/* disabled prop is set to loadingUpdate, because it should not be possible, to click the update button multiple times */}
                Speichern
              </Button>
            </div>
          </Form>
        )}
      </Container>
    </Container>
  );
}
